import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  getBottomBoundary,
  getLeftBoundary,
  getRightBoundary,
  getShapeScreenRect,
  getTopBoundary,
} from '@/lib/editor/floating-canvas-chrome-bounds'

import { GAP, TH, TW } from '../constants/node-edit-toolbar-dimensions'

/**
 * Smart placement against the shape's (un-rotated) screen rect: above, then below, then left,
 * then right.
 */
export function computeNodeEditToolbarPos(
  shape: ShapeData,
  scale: number,
  canvasRect: DOMRect
): { x: number; y: number } {
  const { left: sl, top: st, right: sr, bottom: sb } = getShapeScreenRect(shape, scale, canvasRect)
  const cx = (sl + sr) / 2
  const cy = (st + sb) / 2
  const topBoundary = getTopBoundary()
  const bottomBoundary = getBottomBoundary()

  const leftBoundary = getLeftBoundary()
  const rightBoundary = getRightBoundary()
  // Always land fully inside the live area, even when these shape-relative candidates land
  // off-screen (e.g. the shape being edited has been panned below the visible viewport) —
  // the checks below only pick the best strategy assuming the shape is at least partly
  // visible; they don't guarantee the result is on-screen.
  const clampX = (v: number) => Math.max(leftBoundary + 8, Math.min(rightBoundary - TW - 8, v))
  const clampY = (v: number) => Math.max(topBoundary, Math.min(bottomBoundary - TH - 8, v))

  // Above — preferred position
  const ay = st - TH - GAP

  if (ay >= topBoundary) return { x: clampX(cx - TW / 2), y: clampY(ay) }

  // Below
  const by = sb + GAP

  if (by + TH <= bottomBoundary - 8) return { x: clampX(cx - TW / 2), y: clampY(by) }

  // Left
  const lx = sl - TW - GAP

  if (lx >= leftBoundary + 8) return { x: clampX(lx), y: clampY(cy - TH / 2) }

  // Right — final fallback
  return { x: clampX(sr + GAP), y: clampY(cy - TH / 2) }
}
